import {
  ACCOUNT, PERK_CREATE, PERKS, PERKS_BLOCKED, PERKS_PAGINATE, PERKS_ENABLE,
  PERKS_DISABLE, PERKS_EXCLUDE, PERK_ADD_BLOCKS, PERK_DETAILS, PERK_DETAILS_EMPTY,
  PERK_DETAILS_PATCH, PERK_BLOCK_CATEGORY,
  PERKS_CASHBACK, NOTIFICATIONS_INCLUDE, ADDRESSES
} from '@store/types'
import axios from 'axios'
import shortly from 'vuex-shortly'
import graphQL from '@graphQL'
import PartnerQuery from '@graphQL/queries/Partner'
import PartnersQuery from '@graphQL/queries/Partners'
import PartnersCashbackQuery from '@graphQL/queries/PartnersCashback'
import PartnersBlockedQuery from '@graphQL/queries/PartnersBlocked'
import imageUpload from '@/helpers/fileUpload'

const state = {
  list: [],
  exclusives: [],
  details: {},
  paginate: {},
  categories: []
}

const getters = {
  [PERKS_BLOCKED]: ({ list }) => (list || [])
    .map(partner => ({
      ...partner,
      searchString: ['name', 'id', 'cnpj']
        .map(key => (partner[key] || '')
          .toString().trim().toLowerCase()).join(' ')
    })),
  [PERKS]: 'list',
  [PERKS_PAGINATE]: 'paginate',
  [PERK_DETAILS]: 'details'
}

const mutations = {
  [PERKS]: 'list',
  [PERKS_BLOCKED]: 'list',
  [PERK_DETAILS]: 'details',
  [PERKS_PAGINATE]: 'paginate'
}

const actions = {
  [PERKS]: async ({ commit }, payload = {}) => {
    const response = await graphQL(PartnersQuery, payload)
    const { data: partners, ...paginate } = response?.data?.data?.partners || {}
    commit(PERKS, partners)
    commit(PERKS_PAGINATE, paginate)
  },
  [PERKS_CASHBACK]: async ({ commit }, payload = {}) => {
    const response = await graphQL(PartnersCashbackQuery, payload)
    const { data: partners, ...paginate } = response?.data?.data?.partners || {}
    commit(PERKS, partners)
    commit(PERKS_PAGINATE, paginate)
  },
  [PERKS_BLOCKED]: async ({ commit }, payload = {}) => {
    const response = await graphQL(PartnersBlockedQuery, payload)
    const { data: partners } = response?.data?.data?.partners || {}

    commit(PERKS_BLOCKED, partners)
  },
  [PERK_DETAILS]: async ({ commit }, payload) => {
    const response = await graphQL(PartnerQuery, {
      id: payload.perkId,
      includeAddresses: true,
      includeEdit: payload.isEdit
    })
    if (payload.isEdit && response.data.data?.partner) {
      response.data.data.partner.address = response.data.data?.partner.address || []
    }
    commit(PERK_DETAILS, response.data.data?.partner)

    const addresses = response.data.data?.partner?.address || []
    if (addresses && addresses.length) commit(ADDRESSES, addresses)

    return response
  },
  [PERK_DETAILS_EMPTY]: async ({ commit }) => {
    commit(PERK_DETAILS, {})
  },
  [PERK_DETAILS_PATCH]: async ({ state, commit, dispatch }, { id, payload }) => {
    try {
      Object.keys(payload).forEach((key) => (payload[key] === null || payload[key] === undefined) && delete payload[key])
      const url = `/perks/${id || state.details.id}`

      const { logo, logo_negative: negative, contract, ...perk } = (payload || {}).perk
      const logoDiff = logo && (logo !== state.details.logo)
      const negativeLogoDiff = negative && (negative !== state.details.logo_negative)

      const response = await axios.patch(url, perk)

      const notification = {
        type: response.data.success ? 'success' : 'error',
        message: response.data.success
          ? 'Parceiro modificado com sucesso!'
          : 'Não foi possível atualizar os dados do parceiro'
      }
      dispatch(NOTIFICATIONS_INCLUDE, notification)

      if (logoDiff || negativeLogoDiff) {
        const images = {
          ...!logoDiff || { logo },
          ...!negativeLogoDiff || { logo_negative: negative }
        }
        await imageUpload({ ...images, url })
      }

      if (contract instanceof File) {
        await imageUpload({ contract, url }, 'Contrato importado com sucesso', 'Não foi possível importar o contrato.')
      }
      await dispatch(PERK_DETAILS, { perkId: id, getClient: true })

      return response
    } catch (e) {
      dispatch(NOTIFICATIONS_INCLUDE, new Error('Não foi possível atualizar os dados do parceiro'))
    }
  },
  [PERK_CREATE]: async ({ commit, dispatch }, payload) => {
    const { id, ...params } = payload
    try {
      const url = '/perks'
      const response = await imageUpload(
        { url, ...params, isPost: true, returnAll: true },
        'O parceiro foi criado com sucesso',
        'Não foi possível criar o parceiro'
      )

      return response
    } catch (error) {
      const message = 'Não foi possível criar o parceiro.'
      dispatch(NOTIFICATIONS_INCLUDE, new Error(message))
    }
  },
  [PERKS_ENABLE]: async ({ dispatch }, { account, filters, payload }) => {
    try {
      const perks = payload.map((perk) => perk.id)
      await axios.patch('/perks/group', { status: 1, ids: perks })
      await dispatch(NOTIFICATIONS_INCLUDE, {
        type: 'success',
        message: 'Status alterado com sucesso!'
      })
      await dispatch(PERKS, { account, ...filters })
    } catch (error) {
      await dispatch(NOTIFICATIONS_INCLUDE, Error('Não foi possível alterar o status'))
    }
  },
  [PERKS_DISABLE]: async ({ dispatch }, { account, filters, payload }) => {
    try {
      const perks = payload.map((perk) => perk.id)
      await axios.patch('/perks/group', { status: 0, ids: perks })
      await dispatch(NOTIFICATIONS_INCLUDE, {
        type: 'success',
        message: 'Status alterado com sucesso!'
      })
      await dispatch(PERKS, { account, ...filters })
    } catch (error) {
      await dispatch(NOTIFICATIONS_INCLUDE, Error('Não foi possível alterar o status'))
    }
  },
  [PERKS_EXCLUDE]: async ({ dispatch }, { account, filters, payload }) => {
    try {
      const perks = payload.map((perk) => perk.id)
      await axios.post('/perks/action', { action: 'delete', ids: perks })
      await dispatch(NOTIFICATIONS_INCLUDE, {
        type: 'success',
        message: 'Parceiro(s) excluído(s) com sucesso!'
      })
      await dispatch(PERKS, { account, ...filters })
    } catch (error) {
      await dispatch(NOTIFICATIONS_INCLUDE, Error('Não foi possível excluir o(s) parceiro(s)'))
    }
  },
  [PERK_ADD_BLOCKS]: async ({ commit, dispatch, getters }, payload) => {
    const { id } = getters[ACCOUNT]
    const response = await axios.post(`/company/${id}/block-partners`, payload)

    if (response.data.success) {
      const message = 'Parceiro(s) bloqueado(s) com sucesso!'
      dispatch(NOTIFICATIONS_INCLUDE, { type: 'success', message })
    } else {
      const message = 'Não foi possível bloquear o(s) parceiro(s).'
      dispatch(NOTIFICATIONS_INCLUDE, new Error(message))
    }
  },
  [PERK_BLOCK_CATEGORY]: async ({ commit, dispatch, getters }, category) => {
    const { id } = getters[ACCOUNT]

    await axios.post(`/company/${id}/category/${category}/block`)
  }
}

export default shortly({ state, getters, mutations, actions })



// WEBPACK FOOTER //
// ./src/store/modules/perks.js